import { useEffect, useState } from 'react';
import { Ban } from 'lucide-react';
import { Button, Input, Modal, Textarea, useToast } from '../../ui';
import type { Blocker, Task } from '../../features/tasks/types';

export interface MarkBlockedModalProps {
  open: boolean;
  onClose: () => void;
  task: Task | null;
  /** Persists the structured blocker on the task. */
  onBlock: (task: Task, blocker: Blocker) => Promise<void>;
}

/**
 * Marking a task blocked (§17): asks only What's blocking (required), who we're
 * Waiting on (optional), and a Link (optional). Sets the task's structured
 * blocker with a `since` timestamp so the board can show "Blocked for N days".
 */
export function MarkBlockedModal({
  open,
  onClose,
  task,
  onBlock,
}: MarkBlockedModalProps) {
  const { toast } = useToast();
  const [reason, setReason] = useState('');
  const [waitingOn, setWaitingOn] = useState('');
  const [link, setLink] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !task) return;
    setReason(task.blocker?.reason ?? '');
    setWaitingOn(task.blocker?.waitingOn ?? '');
    setLink(task.blocker?.link ?? '');
  }, [open, task]);

  if (!task) return null;

  const submit = async () => {
    if (!reason.trim()) {
      toast({
        tone: 'error',
        title: 'What is blocking?',
        description: 'Describe what is blocking this task.',
      });
      return;
    }
    setSaving(true);
    try {
      const blocker: Blocker = {
        reason: reason.trim(),
        waitingOn: waitingOn.trim() || undefined,
        link: link.trim() || undefined,
        // Preserve the original block time when re-editing an existing blocker.
        since: task.blocker?.since ?? new Date().toISOString(),
      };
      await onBlock(task, blocker);
      toast({ tone: 'success', title: 'Task marked blocked', description: task.title });
      onClose();
    } catch (e) {
      toast({
        tone: 'error',
        title: 'Couldn’t mark blocked',
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={saving ? () => undefined : onClose}
      size="md"
      title={
        <span className="flex items-center gap-2">
          <Ban className="h-4 w-4 text-red-400" aria-hidden="true" />
          Mark blocked · <span className="font-normal text-zinc-400">{task.title}</span>
        </span>
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="danger" loading={saving} onClick={submit}>
            Mark blocked
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Textarea
          label="What's blocking?"
          rows={3}
          value={reason}
          placeholder="e.g. Waiting on the schema review before the migration can land."
          onChange={(e) => setReason(e.target.value)}
        />
        <Input
          label="Waiting on (optional)"
          value={waitingOn}
          placeholder="alias or team"
          onChange={(e) => setWaitingOn(e.target.value)}
        />
        <Input
          label="Link (optional)"
          value={link}
          placeholder="ticket, doc, or CR URL"
          onChange={(e) => setLink(e.target.value)}
        />
      </div>
    </Modal>
  );
}

export default MarkBlockedModal;
